import { useMemo } from 'react';
import { CircleCheck, CircleDashed, CircleX, Laptop, PlaneTakeoff } from 'lucide-react';

import type { Migration, MigrationDevice } from '../api/migrations';
import { MetricCard, SectionPanel, StatePill } from './abPrimitives';
import WaveFailedDevicesPanel from './WaveFailedDevicesPanel';
import {
  type CompletionCurve,
  type Distribution,
  deriveCompletionCurve,
  deriveLifecycleTally,
  derivePhaseDistribution,
  deriveTerminalDistribution,
} from '../lib/migrationProgress';
import { ACCENT, NUMERIC, STATE, STROKE, SURFACE, TEXT } from '../theme/tokens';

// The operational view (FD-498, mockup 9): what the wave is actually doing.
//
// ── WHEN IT RENDERS, AND WHY THE TRIGGER HAS TWO ARMS ──────────────────────
//
// `isOperationalCampaign` below is the whole trigger, and it is ONE view with
// TWO entry points rather than two surfaces that would drift:
//
//   * a wave with devices under way — the mockup's own case
//   * a REVOKED wave — added by the FD-498 amendment, because FD-492 correctly
//     removed the per-status histogram from the draft Overview and that left a
//     FINISHED campaign with no outcome distribution anywhere. How many of 500
//     devices completed versus salvaged versus faulted is the single most useful
//     thing to know once a wave is over, and `WaveFailedDevicesPanel` answers
//     only the second half of it.
//
// ON A REVOKED WAVE THE DISTRIBUTION IS A TERMINAL-OUTCOME ONE AND THE HEADING
// SAYS SO. A panel headed "Migration in progress" over a finished wave is the
// FD-269 class of defect — a surface reporting a state it can see is not true.
//
// ── WHAT IS NOT HERE, AND MUST NOT BE ADDED ────────────────────────────────
//
// **No Pause and no Stop.** Mockup 9 draws both in its quick-actions card and
// the handoff calls them placeholders itself. `abort-wave` is irreversible, it
// terminal-stamps every non-started device, and it does not stop a device that
// is already migrating — so wiring either label to it would be a data-loss
// defect wearing a friendly word, which is exactly what FD-ADR-031 §6 refused.
// Complete and Abort stay in `WavePanel`, with their own dialogs and their
// `REVOKE_RETRY` handling.
//
// **No per-device Duration.** There is no start timestamp anywhere on the wire.
// `lastEventTimestamp` is LAST CONTACT, not elapsed migration time.
//
// **No percentages.** Every proportion here is drawn as a bar rather than
// printed, so no figure on this panel can disagree with the count beside it.

/**
 * Should the campaign show the operational view rather than the FD-492 draft one?
 *
 * `currentIndex === 5` — the `complete` step of the FD-490 spine — is exactly
 * "at least one device has started, OR the wave is revoked". Both arms of the
 * trigger, in one expression, from the derivation that already owns the rule.
 *
 * IT NEVER FLIPS BACK, which is the property that makes it usable: the spine is
 * monotone by furthest evidence, so a campaign that reaches this step stays
 * there. A trigger keyed on the `migrate` step (index 4) would show the panel
 * only in the window between the package being built and the FIRST device
 * starting, and then swap it away for the rest of the wave.
 */
export function isOperationalCampaign(currentIndex: number): boolean {
  return currentIndex >= 5;
}

export interface MigrationProgressPanelProps {
  migration: Migration;
  devices: MigrationDevice[];
}

function isRevoked(migration: Pick<Migration, 'bprtStatus'>): boolean {
  return (migration.bprtStatus ?? '').trim().toLowerCase() === 'revoked';
}

export default function MigrationProgressPanel({
  migration,
  devices,
}: MigrationProgressPanelProps) {
  const complete = isRevoked(migration);

  const tally = useMemo(() => deriveLifecycleTally(devices), [devices]);
  const distribution = useMemo(
    () => (complete ? deriveTerminalDistribution(devices) : derivePhaseDistribution(devices)),
    [complete, devices],
  );
  const curve = useMemo(() => deriveCompletionCurve(devices), [devices]);

  return (
    <section className="mb-6" aria-label={complete ? 'Wave outcome' : 'Migration in progress'}>
      <div
        className="mb-4 flex flex-wrap items-start gap-4 rounded-lg border px-5 py-4"
        style={{ borderColor: STROKE.base, backgroundColor: SURFACE.base }}
      >
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md"
          style={{
            backgroundColor: complete ? STATE.success.soft : ACCENT.tint,
            color: complete ? STATE.success.text : ACCENT.pressed,
          }}
        >
          {complete ? (
            <CircleCheck size={22} strokeWidth={1.5} />
          ) : (
            <PlaneTakeoff size={22} strokeWidth={1.5} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="m-0 text-[17px] font-semibold" style={{ color: TEXT.primary }}>
              {complete ? 'Wave complete' : 'Migration in progress'}
            </h2>
            <StatePill state={complete ? 'success' : 'progress'}>
              {complete ? 'Credential revoked' : 'Wave active'}
            </StatePill>
          </div>
          <p
            className="mt-1 max-w-[65ch] text-[13px] leading-relaxed"
            style={{ color: TEXT.secondary }}
          >
            {complete
              ? 'The wave credential has been revoked, so no device can fetch a flight plan from it. These are the outcomes the devices reported.'
              : 'Devices are fetching their flight plans and migrating to the destination tenant. Each one reports its own progress; nothing here drives them.'}
          </p>
        </div>
      </div>

      {/* The lifecycle tally. FIVE TILES ON THE COMMON CASE — the mockup's row
          exactly — with `Stopped` and `Unrecognised state` rendered only when
          non-zero, the `DeviceReadinessTiles` rule. The visible row therefore
          always sums to the total, because the only hidden buckets are empty
          ones. */}
      <div
        data-testid="migration-progress-tiles"
        className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
      >
        <MetricCard
          label="Devices in this wave"
          value={tally.total.toLocaleString()}
          caption="Every assigned device, whatever state it is in."
          icon={<Laptop size={18} strokeWidth={1.5} />}
        />
        {tally.visible.map((bucket) => (
          <MetricCard
            key={bucket.key}
            label={bucket.label}
            value={bucket.count.toLocaleString()}
            caption={bucket.description}
            tone={bucket.tone}
            icon={bucketIcon(bucket.key)}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <SectionPanel
          title={complete ? 'Outcomes' : 'Device phase'}
          description={
            complete
              ? 'What each device reported when it finished.'
              : 'Where each device was when it last reported an action.'
          }
        >
          <DistributionBars distribution={distribution} />
          {!complete && (
            <p className="mt-3 text-[11px] leading-relaxed" style={{ color: TEXT.secondary }}>
              Read from each device’s last reported action, not from its event trail — one column
              per device rather than one query per device.
            </p>
          )}
        </SectionPanel>

        <SectionPanel
          title="Completions over time"
          description="Cumulative devices that reported a completed outcome."
        >
          <CompletionsChart curve={curve} />
        </SectionPanel>
      </div>

      {/* THE ISSUES SURFACE — and it is rendered HERE ONLY WHILE THE WAVE RUNS.
          `WavePanel` already renders this same panel once the wave is revoked
          (`WavePanel.tsx`, `status === 'revoked'`), and `WavePanel` now lives in
          the shell's context column on EVERY campaign tab (FD-491). Mounting it
          again on a revoked wave would put two copies of one table on one screen
          and fire the same request twice.
          Mid-flight is where it is genuinely new information: nothing else on
          screen answers "which devices have already failed while the rest are
          still flying", and that is the set an operator moves to a supplementary
          campaign (FD-ADR-035). */}
      {!complete && <WaveFailedDevicesPanel migrationId={migration.migrationId} />}
    </section>
  );
}

function bucketIcon(key: string) {
  if (key === 'completed') return <CircleCheck size={18} strokeWidth={1.5} />;
  if (key === 'failed' || key === 'faulted') return <CircleX size={18} strokeWidth={1.5} />;
  if (key === 'inProgress') return <PlaneTakeoff size={18} strokeWidth={1.5} />;
  return <CircleDashed size={18} strokeWidth={1.5} />;
}

/**
 * The distribution, as labelled bars.
 *
 * The bar is a GRAPHIC, so it is painted in the meaning's glyph hex; the label
 * beside it is TEXT and is painted in `TEXT.primary`/`secondary`, never in a
 * glyph hex (every one of those fails as text on its own tint).
 */
function DistributionBars({ distribution }: { distribution: Distribution }) {
  if (distribution.total === 0) {
    return (
      <p className="m-0 text-[13px]" style={{ color: TEXT.secondary }}>
        No devices are assigned to this campaign, so there is nothing to distribute.
      </p>
    );
  }

  return (
    <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
      {distribution.visible.map((bucket) => {
        const share = bucket.count / distribution.total;
        return (
          <li key={bucket.key} className="flex items-center gap-3" title={bucket.description}>
            <span
              className="w-[9.5rem] shrink-0 text-[12px]"
              style={{ color: bucket.count > 0 ? TEXT.primary : TEXT.secondary }}
            >
              {bucket.label}
            </span>
            <span
              className="h-2 flex-1 overflow-hidden rounded-sm"
              style={{ backgroundColor: SURFACE.rowFill }}
              aria-hidden="true"
            >
              <span
                className="block h-full"
                style={{
                  width: `${share * 100}%`,
                  backgroundColor: STATE[bucket.tone].glyph,
                }}
              />
            </span>
            <span
              className="w-10 shrink-0 text-right text-[13px] font-medium"
              style={{ color: TEXT.primary, ...NUMERIC }}
            >
              {bucket.count.toLocaleString()}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

const CHART_WIDTH = 560;
const CHART_HEIGHT = 150;
const PAD_LEFT = 34;
const PAD_RIGHT = 8;
const PAD_TOP = 10;
const PAD_BOTTOM = 22;

/**
 * Cumulative completions.
 *
 * A STEP curve rather than a smoothed line, because the data IS steps: a device
 * completes at an instant, and interpolating between two completions would draw
 * a fractional device. Mockup 9's stacked in-progress and pending bands are
 * deliberately absent — see `deriveCompletionCurve` for why they cannot be drawn
 * honestly.
 */
function CompletionsChart({ curve }: { curve: CompletionCurve }) {
  const { points, completed, undated } = curve;

  if (points.length === 0) {
    return (
      <div className="flex flex-col gap-1.5">
        <p className="m-0 text-[13px]" style={{ color: TEXT.secondary }}>
          {completed === 0
            ? 'No device has reported a completed outcome yet.'
            : 'No completion carries a recorded time, so there is no curve to draw.'}
        </p>
        {undated > 0 && completed > 0 && <UndatedNote undated={undated} />}
      </div>
    );
  }

  const first = points[0].time;
  const last = points[points.length - 1].time;
  const span = last - first;
  const top = points[points.length - 1].cumulative;

  const innerWidth = CHART_WIDTH - PAD_LEFT - PAD_RIGHT;
  const innerHeight = CHART_HEIGHT - PAD_TOP - PAD_BOTTOM;
  // A single completion, or several at the same instant, has no span. Pin those
  // to the left edge rather than dividing by zero, and let the final segment run
  // out to the right so the step is still visible.
  const x = (time: number) =>
    PAD_LEFT + (span === 0 ? 0 : ((time - first) / span) * innerWidth);
  const y = (value: number) => PAD_TOP + innerHeight - (value / top) * innerHeight;

  const commands: string[] = [`M ${x(first)} ${y(0)}`];
  for (const point of points) {
    commands.push(`L ${x(point.time)} ${y(point.cumulative - 1)}`);
    commands.push(`L ${x(point.time)} ${y(point.cumulative)}`);
  }
  commands.push(`L ${PAD_LEFT + innerWidth} ${y(top)}`);

  const summary = `Cumulative completions: ${top} ${top === 1 ? 'device' : 'devices'} between ${formatStamp(points[0].iso)} and ${formatStamp(points[points.length - 1].iso)}.`;

  return (
    <div className="flex flex-col gap-1.5">
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        className="w-full"
        style={{ height: 'auto' }}
        role="img"
        aria-label={summary}
      >
        {/* Two gridlines and a baseline — 1px strokes, no fills, no shadow. */}
        {[0, top].map((value) => (
          <g key={value}>
            <line
              x1={PAD_LEFT}
              x2={CHART_WIDTH - PAD_RIGHT}
              y1={y(value)}
              y2={y(value)}
              stroke={STROKE.subtle}
              strokeWidth={1}
            />
            <text
              x={PAD_LEFT - 6}
              y={y(value) + 3.5}
              textAnchor="end"
              fontSize="10"
              fill={TEXT.secondary}
              style={NUMERIC}
            >
              {value}
            </text>
          </g>
        ))}
        <path
          d={commands.join(' ')}
          fill="none"
          // The accent as a GRAPHIC — `ACCENT.base` is the graphical rung and
          // clears WCAG 1.4.11 on both the surface and the canvas. The text rung
          // would be the wrong value in the wrong role.
          stroke={ACCENT.base}
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <text x={PAD_LEFT} y={CHART_HEIGHT - 6} fontSize="10" fill={TEXT.secondary}>
          {formatStamp(points[0].iso)}
        </text>
        <text
          x={CHART_WIDTH - PAD_RIGHT}
          y={CHART_HEIGHT - 6}
          textAnchor="end"
          fontSize="10"
          fill={TEXT.secondary}
        >
          {formatStamp(points[points.length - 1].iso)}
        </text>
      </svg>
      {undated > 0 && <UndatedNote undated={undated} />}
    </div>
  );
}

/**
 * A completed device whose `terminalUtc` is missing or unparseable is COUNTED
 * AND NAMED rather than dropped: a curve that quietly ends below the Completed
 * tile beside it is a chart an operator can falsify.
 */
function UndatedNote({ undated }: { undated: number }) {
  return (
    <p className="m-0 text-[11px] leading-relaxed" style={{ color: TEXT.secondary }}>
      {undated === 1
        ? '1 completed device carries no recorded completion time and is not on the curve.'
        : `${undated} completed devices carry no recorded completion time and are not on the curve.`}
    </p>
  );
}

function formatStamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
